import {
	faArrowLeft,
	faCircleCheck,
	faHeadset,
	faPaperPlane,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateTime } from '@supply-mover-2-0/core';
import {
	issueRichTextToPlainText,
	IssueStatus,
	PORTAL_ISSUE_MESSAGE_MAX_LENGTH,
	type PortalIssueMessageDto,
} from '@supply-mover-2-0/shared';
import {
	issueImageFileIds,
	IssueImagePicker,
	issueImagesUploading,
	IssueImageThumbnails,
	IssueRichTextContent,
	IssueRichTextEditor,
	useTranslation,
	type PendingIssueImageModel,
} from '@supply-mover-2-0/web-ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { EmptyState } from '../components/EmptyState';
import { IssueStatusChip } from '../components/IssueStatusChip';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { HistoryListSkeleton } from '../components/skeletons';
import { SupportNotifyControl } from '../components/SupportNotifyControl';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as portalIssueService from '../services/portalIssueService';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * One response on the ticket (PD-15002) — a full-width row rather than a
 * chat bubble: author + timestamp on top, the message below. Support
 * responses carry an accent edge + tag so the two sides stay easy to
 * tell apart without the chat-style alignment.
 */
function ResponseRow({
	message,
}: {
	message: PortalIssueMessageDto;
}): JSX.Element {
	const { t } = useTranslation();
	const isCustomer = message.author === 'customer';

	return (
		<div
			className={`bg-brand-surface border border-brand-border rounded-xl p-3 ${
				isCustomer ? '' : 'border-l-4 border-l-portal-accent'
			}`}
		>
			<div className="flex items-center justify-between gap-2 mb-1">
				<span className="text-xs font-medium text-default flex items-center gap-1.5">
					{isCustomer
						? t('You')
						: message.authorName != null
							? t(message.authorName)
							: t('Support team')}
					{!isCustomer && (
						<span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-portal-accent/10 text-portal-accent">
							{t('Support')}
						</span>
					)}
				</span>
				<span className="text-xs text-brand-muted">
					{formatDateTime(message.createdDate as unknown as string)}
				</span>
			</div>
			<IssueRichTextContent
				className="text-sm text-default"
				html={message.bodyHtml}
				text={message.body}
			/>
			{/* PD-16475 — images sent with the message (signed by the API). */}
			<IssueImageThumbnails
				className="mt-2"
				attachments={message.attachments}
			/>
		</div>
	);
}

/**
 * The line above the reply box that tells the customer where the request
 * stands and what a reply will do (PD-16525). Null when there is nothing to
 * add to the status chip.
 */
function replyHint(status: string): string | null {
	switch (status as IssueStatus) {
		case IssueStatus.WAITING_ON_CUSTOMER:
			return 'Our team is waiting on your reply.';
		case IssueStatus.RESOLVED:
			return 'Replying will reopen this request for the support team.';
		case IssueStatus.DONE:
			return 'This request is closed. Replying will reopen it for the support team.';
		default:
			return null;
	}
}

/**
 * Support request detail (PD-13773, reworked in PD-15002). The ticket
 * reads as a card — subject, reference, category, status and the original
 * submission — followed by a flat Responses list and a reply box. Any reply
 * hands the request back to the support team (Needs Reply), whatever its
 * status was.
 *
 * PD-16525 — when the team marks a request Resolved, the page asks whether
 * that solved it: "This solved it" closes the request, "I still need help"
 * hands it back to the team and moves the customer to the reply box.
 */
export function SupportIssueDetailPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const params = useParams<{ id: string }>();
	const issueId = params.id != null ? parseInt(params.id, 10) : null;
	const { token, isAuthenticated } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const queryClient = useQueryClient();
	// PD-16525 — "I still need help" moves the customer to the reply box.
	const replyBoxRef = useRef<HTMLDivElement>(null);

	// PD-16475 — the reply as rich text, plus images.
	const [replyHtml, setReplyHtml] = useState('');
	const [replyImages, setReplyImages] = useState<Array<PendingIssueImageModel>>(
		[],
	);

	const uploadImage = useCallback(
		async (file: File) => {
			if (token == null) {
				throw new Error(t('Please sign in again.'));
			}

			return portalIssueService.uploadPortalIssueImage(token, file);
		},
		[token, t],
	);

	const { data, isLoading, isError } = useQuery({
		queryKey: ['portal-issue', issueId],
		queryFn: () => portalIssueService.getPortalIssue(token ?? '', issueId ?? 0),
		enabled: token != null && issueId != null,
		staleTime: 15_000,
	});

	const replyMutation = useMutation({
		mutationFn: (payload: {
			message: string;
			messageHtml: string;
			attachmentFileIds: Array<number>;
		}) =>
			portalIssueService.replyToPortalIssue(token ?? '', issueId ?? 0, payload),
		onSuccess: (response) => {
			// The reply response carries the refreshed thread — swap it in and
			// invalidate the list so its message counts / status stay current.
			queryClient.setQueryData(['portal-issue', issueId], response.detail);
			void queryClient.invalidateQueries({ queryKey: ['portal-issues'] });
			setReplyHtml('');
			setReplyImages([]);
		},
	});

	const confirmMutation = useMutation({
		mutationFn: (solved: boolean) =>
			portalIssueService.confirmPortalIssueResolution(
				token ?? '',
				issueId ?? 0,
				{ solved },
			),
		onSuccess: (response, solved) => {
			queryClient.setQueryData(['portal-issue', issueId], response.detail);
			void queryClient.invalidateQueries({ queryKey: ['portal-issues'] });

			if (!solved) {
				replyBoxRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});
				replyBoxRef.current
					?.querySelector<HTMLElement>('[contenteditable="true"]')
					?.focus();
			}
		},
	});

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	// The server counts the visible text, not the markup, against the limit.
	const replyText = issueRichTextToPlainText(replyHtml);
	const replyFileIds = issueImageFileIds(replyImages);
	const canSend =
		(replyText.trim().length > 0 || replyFileIds.length > 0) &&
		replyText.length <= PORTAL_ISSUE_MESSAGE_MAX_LENGTH &&
		!issueImagesUploading(replyImages) &&
		!replyMutation.isPending;

	const handleSend = (): void => {
		if (canSend) {
			replyMutation.mutate({
				message: replyText,
				messageHtml: replyHtml,
				attachmentFileIds: replyFileIds,
			});
		}
	};

	// The synthetic id-0 entry is the customer's original submission (it
	// lives on the issue, not in issue_note) — it renders inside the ticket
	// card; everything else is a response.
	const original = data?.thread.find((message) => message.id === 0) ?? null;
	const responses = data?.thread.filter((message) => message.id !== 0) ?? [];

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Support" backPath="/support" />
			<AccountBreadcrumb
				parent={{ label: 'Support', path: '/support' }}
				current={data != null ? `#${data.id}` : t('Request')}
			/>

			{/* Desktop way back to the list (PD-15002); mobile has the
			    MobilePageHeader arrow. */}
			<button
				type="button"
				onClick={() => {
					void navigate('/support');
				}}
				className="hidden md:inline-flex items-center gap-1.5 text-sm text-portal-accent hover:underline mb-3"
			>
				<FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
				{t('All Support Requests')}
			</button>

			{isLoading ? (
				<HistoryListSkeleton />
			) : isError || data == null ? (
				<EmptyState
					icon={faHeadset}
					title="Support request not found"
					hint="It may have been removed, or the link is incorrect."
				/>
			) : (
				<>
					{/* Ticket card: subject, reference, category, status, and the
					    original submission */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
						<div className="flex items-start justify-between gap-3">
							<h1 className="text-lg md:text-xl font-bold text-default">
								{t(data.name)}
							</h1>
							<IssueStatusChip status={data.status} />
						</div>
						<div className="text-xs text-brand-muted mt-1">
							{t('#')}
							{data.id}
							{data.type != null && (
								<>
									{' · '}
									{t(data.type)}
								</>
							)}
							{' · '}
							{t('Submitted')}{' '}
							{formatDateTime(data.createdDate as unknown as string)}
						</div>
						{original != null && (
							<div className="mt-3 pt-3 border-t border-brand-border">
								<IssueRichTextContent
									className="text-sm text-default"
									html={original.bodyHtml}
									text={original.body}
								/>
								<IssueImageThumbnails
									className="mt-2"
									attachments={original.attachments}
								/>
							</div>
						)}
					</div>

					{/* Responses */}
					<h2 className="text-sm font-semibold text-default mb-2">
						{t('Responses')}
						{responses.length > 0 && (
							<span className="text-brand-muted font-normal">
								{' '}
								({responses.length})
							</span>
						)}
					</h2>
					<div className="flex flex-col gap-2 mb-4">
						{responses.map((message) => (
							<ResponseRow key={message.id} message={message} />
						))}
						{responses.length === 0 && (
							<p className="text-sm text-brand-muted">
								{t('No responses yet. Our team will follow up here.')}
							</p>
						)}
					</div>

					{/* PD-16525 — the team marked it Resolved: did that solve it? */}
					{(data.status as IssueStatus) === IssueStatus.RESOLVED && (
						<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
							<div className="flex items-start gap-3">
								<FontAwesomeIcon
									icon={faCircleCheck}
									className="h-5 w-5 text-success mt-0.5 shrink-0"
								/>
								<div className="min-w-0 flex-1">
									<p className="text-sm font-semibold text-default">
										{t('We marked this request resolved.')}
									</p>
									<p className="text-sm text-brand-muted mt-0.5">
										{t('Did that solve it for you?')}
									</p>
									<div className="flex flex-wrap gap-2 mt-3">
										<button
											type="button"
											onClick={() => {
												confirmMutation.mutate(true);
											}}
											disabled={confirmMutation.isPending}
											className="px-4 py-2 rounded-lg bg-portal-accent text-white text-sm font-medium hover:bg-portal-accent-hover disabled:opacity-50 min-h-touch"
										>
											{t('This solved it')}
										</button>
										<button
											type="button"
											onClick={() => {
												confirmMutation.mutate(false);
											}}
											disabled={confirmMutation.isPending}
											className="px-4 py-2 rounded-lg border border-brand-border text-sm font-medium text-default hover:bg-brand-input disabled:opacity-50 min-h-touch"
										>
											{t('I still need help')}
										</button>
									</div>
									{confirmMutation.isError && (
										<p className="text-xs text-danger mt-2">
											{t(
												"We couldn't save your answer right now. Please try again.",
											)}
										</p>
									)}
								</div>
							</div>
						</div>
					)}

					{/* Reply box */}
					<div
						ref={replyBoxRef}
						className="bg-brand-surface border border-brand-border rounded-xl p-3"
					>
						{replyHint(data.status) != null && (
							<p className="text-xs text-brand-muted mb-2">
								{t(replyHint(data.status) ?? '')}
							</p>
						)}
						<IssueRichTextEditor
							value={replyHtml}
							onChange={setReplyHtml}
							placeholder={t('Write a reply...')}
							disabled={replyMutation.isPending}
							minHeight="80px"
							className="bg-brand-input border-brand-border"
						/>
						<IssueImagePicker
							className="mt-2"
							value={replyImages}
							onChange={setReplyImages}
							upload={uploadImage}
							disabled={replyMutation.isPending}
						/>
						{/* PD-16475 — how to be told about replies; saves on change. */}
						<SupportNotifyControl mode="immediate" className="mt-3" />
						{replyMutation.isError && (
							<p className="text-xs text-danger mt-1">
								{t("We couldn't send your reply right now. Please try again.")}
							</p>
						)}
						<div className="flex justify-end mt-2">
							<button
								type="button"
								onClick={handleSend}
								disabled={!canSend}
								className="px-4 py-2 rounded-lg bg-portal-accent text-white text-sm font-medium hover:bg-portal-accent-hover disabled:opacity-50 flex items-center gap-2 min-h-touch"
							>
								<FontAwesomeIcon icon={faPaperPlane} />
								{replyMutation.isPending ? t('Sending...') : t('Send Reply')}
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
